import { DollarOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Card,
  Col,
  Input,
  Row,
  Select,
  Statistic,
  Table,
  type TableProps,
} from 'antd';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageTitle } from '@client/components/PageTitle';
import { HideColumns } from '@client/components/table/HideColumns';
import { Toolbar } from '@client/components/Toolbar';
import { EAbilityActions, EAbilitySubjects } from '@client/hooks/useAbilities';
import { useAuth } from '@client/hooks/useAuth';
import { useColumnsToggle } from '@client/hooks/useColumnsToggle';
import { useCredits } from '@client/hooks/useCredits';
import { useTable } from '@client/hooks/useTable';
import { PageLayout } from '@client/layouts/page.layout';
import { columnsMetadata } from '@client/pages/credits/columns.metadata';
import { GrantCreditsModal } from '@client/pages/credits/components/grant-credits.modal';
import { useTenantQueries } from '@client/queries/useTenantQueries';
import { type IGrantCreditsPayload } from '@client/services/credits.service';
import { useLocale } from '@client/store/locale.store';
import { indexable } from '@client/utils/table/table.util';

import type { TColumns } from '@client/types/antd.type';
import type { ILedgerEntry } from '@common/types';
import type { ItemType } from 'antd/es/menu/interface';

export const CreditsPage: React.FC = () => {
  const { ability } = useAuth();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const [grantModalOpen, setGrantModalOpen] = useState(false);
  const [filter, setFilter] = useState<{
    tenantId?: string;
    userId?: string;
  }>({});
  const [userIdInput, setUserIdInput] = useState('');

  const { useAllTenants } = useTenantQueries();
  const { data: tenants, isLoading: tenantsLoading } = useAllTenants();

  const { loading, wallet, entries, total, onGrant, refetch } =
    useCredits(filter);

  const {
    tableParams: { pagination },
    filteredInfo,
    sortedInfo,
    handleTableChange,
  } = useTable<ILedgerEntry>(entries, total, { syncUrl: false });

  const columns: TableProps<ILedgerEntry>['columns'] = columnsMetadata({
    locale,
    filteredInfo,
    sortedInfo,
    entities: entries,
  });

  const { filteredColumns, columnsList, selectedColumns, setSelectedColumns } =
    useColumnsToggle<ILedgerEntry>(columns as TColumns<ILedgerEntry>[], []);

  const handleGrant = async (
    values: Omit<IGrantCreditsPayload, 'idempotencyKey'>,
  ): Promise<void> => {
    await onGrant(values);
    setGrantModalOpen(false);
    setUserIdInput(values.userId);
    setFilter({ tenantId: values.tenantId, userId: values.userId });
  };

  const tableProps = {
    columns: filteredColumns as TColumns<ILedgerEntry>[],
    pagination,
    scroll: { x: 1000 },
    bordered: true,
    size: 'small' as const,
    dataSource: indexable<ILedgerEntry>(entries),
    loading,
    rowKey: (record: ILedgerEntry) => record.id,
    onChange: handleTableChange,
    title: () => (
      <Toolbar<ILedgerEntry[]>
        onRefresh={refetch}
        entities={entries}
        subject={EAbilitySubjects.CREDITS}
        items={
          [
            {
              disabled: ability.cannot(
                EAbilityActions.MANAGE,
                EAbilitySubjects.CREDITS,
              ),
              label: 'Grant Credits',
              icon: <PlusOutlined />,
              onClick: () => setGrantModalOpen(true),
            },
          ] as unknown as ItemType[]
        }
      >
        <HideColumns
          columnsList={columnsList}
          selectedColumns={selectedColumns}
          onChange={setSelectedColumns}
        />
      </Toolbar>
    ),
    footer: () => <div>{t('table.total', { amount: total })}</div>,
  };

  return (
    <PageLayout
      subject={EAbilitySubjects.CREDITS}
      title={<PageTitle Icon={DollarOutlined} name="Credits" />}
    >
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={12}>
          <Select
            allowClear
            showSearch={{ optionFilterProp: 'label' }}
            loading={tenantsLoading}
            placeholder={t('form.credits.tenant.placeholder')}
            value={filter.tenantId}
            onChange={(val) => {
              setUserIdInput('');
              setFilter(val ? { tenantId: val } : {});
            }}
            style={{ width: '100%' }}
            options={(tenants ?? []).map((tenant) => ({
              value: tenant.id as string,
              label: tenant.company
                ? `${tenant.name} (${tenant.company})`
                : tenant.name,
            }))}
          />
        </Col>
        {ability.can(EAbilityActions.MANAGE, EAbilitySubjects.CREDITS) && (
          <Col span={12}>
            <Input.Search
              allowClear
              placeholder={t('form.credits.userId.placeholder')}
              value={userIdInput}
              onChange={(e) => setUserIdInput(e.target.value)}
              onSearch={(value) =>
                setFilter((prev) => ({ ...prev, userId: value || undefined }))
              }
            />
          </Col>
        )}
      </Row>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Balance"
              value={wallet?.balance ?? 0}
              suffix={wallet?.currency ?? ''}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Held Credits"
              value={wallet?.heldCredits ?? 0}
              suffix={wallet?.currency ?? ''}
            />
          </Card>
        </Col>
      </Row>

      <Table<ILedgerEntry> {...tableProps} loading={loading} />

      <GrantCreditsModal
        open={grantModalOpen}
        loading={loading}
        onCancel={() => setGrantModalOpen(false)}
        onFinish={handleGrant}
      />
    </PageLayout>
  );
};
